import {
  compareAsc,
  differenceInHours,
  differenceInSeconds,
  eachDayOfInterval,
  format as formatDate,
  max,
  min,
  parseISO,
} from 'date-fns'
import type { AbsenceNode } from '@/modules/timesheets/types.graphql'
import { generateRandomColor } from '@/utils/generators'
import type { DriverActivityNode } from './types.graphql'

export function getTotalWorkingHoursFromActivities(
  activities: DriverActivityNode[]
) {
  if (activities.length === 0) {
    return 0
  }
  return activities
    .reduce((acc, activity) => {
      if (!activityCountsAsWorking(activity)) return acc
      return acc + getActivityDurationWithDecimals(activity)
    }, 0)
    .toFixed(1)
}

export function getDrivingHoursFromActivities(
  activities: DriverActivityNode[]
) {
  if (activities.length === 0) {
    return 0
  }
  return activities
    .reduce((acc, activity) => {
      if (activity.activityType.toLowerCase() !== 'drive') return acc
      return acc + getActivityDurationWithDecimals(activity)
    }, 0)
    .toFixed(1)
}

export function getRestingHoursFromActivities(
  activities?: DriverActivityNode[]
) {
  if (typeof activities === 'undefined' || activities.length === 0) {
    return 0
  }
  return activities.reduce((acc, activity) => {
    if (activity.activityType.toLowerCase() !== 'rest') return acc
    return acc + getActivityDurationWithDecimals(activity)
  }, 0)
}

export function getEarliestDatetimeFromActivities(
  activities: DriverActivityNode[]
) {
  if (activities.length === 0) {
    return new Date()
  }

  return min(activities.map(activity => parseISO(activity.datetimeStart)))
}

export function stripLeadingAndTrailingRests(activities: DriverActivityNode[]) {
  let startFilterIndex = 0
  let endFilterIndex = activities.length

  // Ensure activities are sorted by start dates
  activities = activities.sort((a, b) =>
    compareAsc(parseISO(a.datetimeStart), parseISO(b.datetimeEnd))
  )

  // Find the first non-rest, where we should start including activities.
  for (let i = 0; i < activities.length; ++i) {
    const activity = activities[i]
    if (typeof activity === 'undefined') continue

    if (activity.activityType !== 'Rest') {
      startFilterIndex = i
      break
    }
  }

  // Find the last non-rest, where we should start including activities.
  for (let i = activities.length - 1; i > 0; --i) {
    const activity = activities[i]
    if (typeof activity === 'undefined') continue

    if (activity.activityType !== 'Rest') {
      endFilterIndex = i
      break
    }
  }

  return activities.slice(startFilterIndex, endFilterIndex + 1)
}

export function getLatestDatetimeFromActivities(
  activities: DriverActivityNode[]
) {
  if (activities.length === 0) {
    return new Date()
  }
  return max(activities.map(activity => parseISO(activity.datetimeEnd)))
}

export function getColorFromActivity(
  activity: Pick<
    DriverActivityNode,
    'activityType' | 'excludeFromSalaryCalculation'
  >
) {
  if (
    !activity ||
    !activity.activityType ||
    activity.excludeFromSalaryCalculation
  ) {
    return 'tertiary'
  }
  switch (activity.activityType.toLowerCase()) {
    case 'drive':
      return 'primary'
    case 'rest':
      return 'activitiesGreen'
    case 'work':
      return 'orange'
    case 'paid rest':
      return 'activitiesGreen'
    case 'issue':
      return 'activitiesRed'
    default:
      return 'tertiary'
  }
}

export function getTableColumnFromActivity(activity: DriverActivityNode) {
  if (!activity || !activity.activityType) {
    return -1
  }
  switch (activity.activityType.toLowerCase()) {
    case 'drive':
      return 2
    case 'rest':
      return 3
    case 'work':
      return 4
    case 'issue':
      return 5
    default:
      return -1
  }
}

export function activityCountsAsWorking(activity: DriverActivityNode) {
  if (!activity || !activity.activityType) {
    return false
  }
  switch (activity.activityType.toLowerCase()) {
    case 'drive':
      return true
    case 'rest':
      return false
    case 'work':
      return true
    case 'issue':
      return false
    default:
      return false
  }
}

export function getActivityDuration(activity: DriverActivityNode) {
  if (!activity) {
    return 0
  }

  return differenceInHours(
    parseISO(activity.datetimeStart),
    parseISO(activity.datetimeEnd)
  )
}

export function getActivityDurationWithDecimals(activity: DriverActivityNode) {
  if (!activity) {
    return 0
  }

  return Math.abs(
    differenceInSeconds(
      parseISO(activity.datetimeStart),
      parseISO(activity.datetimeEnd)
    )
  )
}

export function durationStringToObject(durationString?: string) {
  if (!durationString) {
    return {
      hours: 0,
      minutes: 0,
      seconds: 0,
    }
  }
  const durationSplit = durationString.split(':')

  return {
    hours: parseInt(durationSplit[0] ?? '0', 10) || 0,
    minutes: parseInt(durationSplit[1] ?? '0', 10) || 0,
    seconds: parseInt(durationSplit[2] ?? '0', 10) || 0,
  }
}

export function secondsToDurationObject(seconds: number) {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds / 60) % 60)
  const _seconds = Math.floor((seconds / 3600) % 60)

  return {
    hours,
    minutes,
    seconds: _seconds,
  }
}

export function stripSecondsFromDuration(
  durationAsString: string | null
): string {
  if (durationAsString === null) {
    return ''
  }

  if (durationAsString.length === 8) {
    return durationAsString.slice(0, 5)
  }
  return durationAsString
}

export function enforceHourMinuteString(
  durationAsString: string | null | undefined
): string {
  if (!durationAsString) return '00:00'

  if (durationAsString.length > 6) {
    return durationAsString.slice(0, durationAsString.length - 3)
  }
  return durationAsString
}

export function createAbsenceMap(absences: AbsenceNode[]) {
  const absenceMap: { [key: string]: string } = {}
  absences.forEach(absence => {
    const intervalDates = eachDayOfInterval({
      start: parseISO(absence.datetimeStart),
      end: parseISO(absence.datetimeEnd),
    }).map(date => formatDate(date, 'yyyy-MM-dd'))

    const absenceText = `${absence.absenceType?.name ?? 'Unknown'}: ${
      absence.reason
    }`
    intervalDates.forEach(date => {
      if (!(date in absenceMap)) {
        absenceMap[date] = absenceText
      }
    })
  })
  return absenceMap
}

export function optimizeActivities(
  activities: Exclude<DriverActivityNode, 'user'>[]
) {
  return activities.reduce<Exclude<DriverActivityNode, 'user'>[]>(
    (acc, cur) => {
      if (acc.length === 0) return [cur]

      const last = acc[acc.length - 1]
      if (!last) return acc

      if (
        last.activityType === cur.activityType &&
        last.vehicleVin === cur.vehicleVin &&
        last.manuallyEntered === cur.manuallyEntered &&
        last.datetimeEnd === cur.datetimeStart
      ) {
        acc[acc.length - 1] = {
          ...last,
          vehicleRegistrationNumber:
            last.vehicleRegistrationNumber ?? cur.vehicleRegistrationNumber,
          datetimeEnd: cur.datetimeEnd,
        }
      } else {
        return acc.concat([cur])
      }
      return acc
    },
    []
  )
}

export function generateActivityMapColors(numberOfColors: number) {
  const colors = []
  for (let i = 0; i <= numberOfColors; i++) {
    if (i === 0) {
      colors.push('#0088FF')
    } else if (i === 1) {
      colors.push('hsl(276deg, 80%, 80%)')
    } else if (i === 2) {
      colors.push('hsl(1151299deg, 84%, 76%)')
    } else if (i === 3) {
      colors.push('hsl(216720deg, 81%, 77%)')
    } else {
      colors.push(generateRandomColor())
    }
  }
  return colors
}

export function parseNumberToTimestamp(number: number): string {
  const time = number.toString().split('.')
  if (time.length === 1) {
    return `${time[0]}:00`.padStart(5, '0')
  }

  const minutes = Math.round(parseFloat(`0.${time[1]}`) * 60)

  return `${time[0]}:${minutes}`.padStart(5, '0')
}

export function parseFloatToTimestamp(number: number): string {
  const time = number.toString().split('.')
  if (time.length === 1) {
    return `${time[0]}:00`.padStart(5, '0')
  }

  const minutes = `${Math.round(parseFloat(`0.${time[1]}`) * 60)}`.padStart(
    2,
    '0'
  )

  return `${time[0]}:${minutes}`.padStart(5, '0')
}
